/**
 * IdeaEntryDialog — modal de entrada de idea para el Asistente Formulador.
 *
 * El usuario describe su idea en lenguaje libre. El asistente la convierte
 * en la estructura de un proyecto defensible (los 6 modulos de
 * identificacion de la MGA).
 *
 * Diseno: overlay slate-900/40 con card centrado. Pill "Asistente
 * Formulador" (brand) + "Pro" (emerald). CTA emerald. Sin gradientes IA.
 * Patron de modal identico a ShowYourWorkModal (cierre ESC + click overlay).
 *
 * Al exito, navega a `/expedientes/:project_id` con `formulationId` en
 * `location.state` como hint para scroll inicial.
 *
 * Cupo de expedientes (issue #568): `POST /formulations` crea un
 * expediente, asi que topa contra el mismo limite del plan que el Radar.
 * El dialogo avisa y deshabilita el envio cuando el plan ya reporta el
 * cupo agotado, y traduce el 402 con enlace a `/membresia`. El bloqueo
 * previo falla abierto (ver `useActiveProjectsQuota`): si el plan no
 * cargo, el envio queda habilitado y decide el backend.
 *
 * Lo que promete depende del plan (issue #629): en uno sin asistente IA
 * (Esencial) no dice que "el asistente la convierte" ni lleva la pastilla
 * "Pro"; dice que el usuario redacta cada modulo con guias y los datos del
 * municipio, y nombra lo que Pro agrega como tal. Mientras el plan no carga
 * se muestra el texto de siempre.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { CupoExpedientesNota } from "@/components/CupoExpedientesNota";
import { InlineSpinner } from "@/components/InlineSpinner";
import { textoCupoAgotado } from "@/lib/cupo-expedientes";
import { ApiError } from "@/lib/errors";
import { useActiveProjectsQuota } from "@/lib/hooks/useActiveProjectsQuota";
import { useCreateFormulation } from "@/lib/hooks/useFormulations";

const MODULE_CHIPS = [
  "Problema",
  "Causas y efectos",
  "Población",
  "Objetivo",
  "Alternativas",
  "Productos",
];

const MIN_IDEA_LENGTH = 10;
const MAX_IDEA_LENGTH = 2000;

interface IdeaEntryDialogProps {
  municipality: string;
  onClose: () => void;
}

export function IdeaEntryDialog({ municipality, onClose }: IdeaEntryDialogProps) {
  const [idea, setIdea] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const navigate = useNavigate();
  const createMutation = useCreateFormulation();
  const cupo = useActiveProjectsQuota();
  const sinIA = cupo.includesAiAssistant === false;

  // Focus en el textarea al abrir y bloquear scroll del body.
  useEffect(() => {
    textareaRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Cierre con ESC.
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const trimmedIdea = idea.trim();
  const isValid = trimmedIdea.length >= MIN_IDEA_LENGTH && trimmedIdea.length <= MAX_IDEA_LENGTH;
  // `POST /formulations` solo devuelve 402 por cupo de expedientes
  // agotado, asi que el status alcanza para discriminarlo.
  const cupoError = createMutation.error instanceof ApiError && createMutation.error.status === 402;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || createMutation.isPending || cupo.isExhausted) return;

    // `mutateAsync` rechaza cuando el backend responde con error. Sin este
    // catch la promesa quedaba sin manejar (el estado de error ya se pinta
    // abajo desde `createMutation`, asi que no hay nada mas que hacer aca).
    let result;
    try {
      result = await createMutation.mutateAsync({ municipality, ideaText: trimmedIdea });
    } catch {
      return;
    }

    onClose();
    navigate(`/expedientes/${result.project_id}`, {
      state: { formulationId: result.id },
    });
  }

  return (
    <div
      data-testid="idea-entry-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="idea-dialog-title"
      className="fixed inset-0 z-100 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="relative my-auto w-full max-w-2xl rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Boton cerrar */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:ring-2 focus:ring-slate-400 focus:ring-offset-1 focus:outline-hidden"
        >
          <svg aria-hidden="true" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
          </svg>
        </button>

        <div className="px-6 pt-6 pb-6">
          {/* Pills */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-brand-50 px-3 py-1 text-[11px] font-semibold tracking-wide text-brand-700 uppercase">
              {sinIA ? "Formulador guiado" : "Asistente Formulador"}
            </span>
            {!sinIA && (
              <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-semibold tracking-wide text-cta-700 uppercase">
                Pro
              </span>
            )}
          </div>

          {/* Titulo y subtitulo */}
          <h2
            id="idea-dialog-title"
            className="text-xl font-extrabold tracking-tight text-slate-900"
          >
            Empieza con una idea.
          </h2>
          <p className="mt-1.5 text-sm text-slate-600">
            {sinIA
              ? "Descríbela en tus palabras. Kaptar la organiza en los módulos de identificación de la MGA, con guías y los datos reales de tu municipio. Tú redactas cada módulo."
              : "Descríbela en tus palabras. El asistente la convierte en la estructura de un proyecto defendible, anclada en los datos reales de tu municipio."}
          </p>

          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            {/* Campo de idea */}
            <div>
              <label
                htmlFor="idea-textarea"
                className="mb-1.5 block text-sm font-medium text-slate-900"
              >
                Tu idea
              </label>
              <textarea
                id="idea-textarea"
                ref={textareaRef}
                data-testid="idea-textarea"
                value={idea}
                onChange={(e) => setIdea(e.target.value)}
                rows={10}
                maxLength={MAX_IDEA_LENGTH}
                placeholder="Ej: Construir un acueducto veredal para 5 veredas que llevan 10 años sin agua potable..."
                className="w-full resize-y rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-hidden"
              />
              <div className="mt-1 flex justify-between text-xs text-slate-400">
                <span>
                  {trimmedIdea.length < MIN_IDEA_LENGTH
                    ? `Mínimo ${MIN_IDEA_LENGTH} caracteres`
                    : ""}
                </span>
                <span>
                  {idea.length}/{MAX_IDEA_LENGTH}
                </span>
              </div>
            </div>

            {/* Nota de honestidad */}
            <div className="flex items-start gap-2 rounded-xl bg-slate-50 px-4 py-3">
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                className="mt-0.5 h-4 w-4 shrink-0 text-cta-600"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
                />
              </svg>
              <p className="text-xs text-slate-600">
                {sinIA
                  ? "Con el plan Pro, la IA te propone un primer borrador de cada módulo con los datos de tu municipio."
                  : "La IA no inventa cifras ni datos. Tú confirmas cada módulo antes de usarlo."}
              </p>
            </div>

            {/* Preview de modulos */}
            <div
              aria-label={sinIA ? "Módulos que completarás" : "Módulos que se generarán"}
              className="flex flex-wrap gap-1.5"
            >
              {MODULE_CHIPS.map((label) => (
                <span
                  key={label}
                  className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11px] text-slate-600"
                >
                  {label}
                </span>
              ))}
            </div>

            {/* Cupo agotado: se avisa antes de que el usuario escriba la idea
                y descubra el bloqueo al enviarla (issue #568). */}
            {cupo.isExhausted && cupo.quota && (
              <CupoExpedientesNota mensaje={textoCupoAgotado(cupo.tier, cupo.quota.limit)} />
            )}

            {/* Error de creacion. El 402 del cupo lleva enlace a /membresia
                para no dejar al usuario sin salida dentro del modal. */}
            {cupoError && (
              <CupoExpedientesNota
                tono="error"
                mensaje={(createMutation.error as ApiError).message}
              />
            )}
            {createMutation.isError && !cupoError && (
              <div
                data-testid="idea-dialog-error"
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
              >
                {createMutation.error instanceof Error
                  ? createMutation.error.message
                  : "Error al crear la formulación. Intenta de nuevo."}
              </div>
            )}

            {/* CTA */}
            <button
              type="submit"
              data-testid="idea-dialog-submit"
              disabled={!isValid || createMutation.isPending || cupo.isExhausted}
              className="min-h-[44px] w-full rounded-xl bg-cta-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-cta-600 focus:ring-2 focus:ring-cta-500 focus:ring-offset-2 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
            >
              {createMutation.isPending ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <InlineSpinner tone="brand" />
                  Creando formulación...
                </span>
              ) : (
                "Empezar a formular"
              )}
            </button>
          </form>

          {/* Disclaimer al pie */}
          <p className="mt-4 text-center text-xs text-slate-400">
            Borrador de apoyo. No reemplaza la MGA oficial ni la revisión del OCAD.
          </p>
        </div>
      </div>
    </div>
  );
}
